import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, logoSrc } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Trophy, Plus, LogIn, LogOut, ShieldCheck, Coins, Users, Sparkles, Archive, ArrowLeft, KeyRound, TrendingUp, TrendingDown, Minus as MinusIcon } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RechartTooltip } from "recharts";
import ChangePasswordModal from "../components/ChangePasswordModal";

const PHASE_META_SHORT = [
  { id: 0,  emoji: "🌱", label: "Creazione stagione",        hint: "Nuova stagione appena creata" },
  { id: 1,  emoji: "📥", label: "Import Rose",               hint: "Prepara il file Excel con le rose" },
  { id: 2,  emoji: "🛍️", label: "Calcolo Merch Base",        hint: "Nessuna azione richiesta" },
  { id: 3,  emoji: "🔄", label: "Mercato di Riparazione",     hint: "Registra trasferimenti / import diff" },
  { id: 4,  emoji: "🏁", label: "Fine Campionato",            hint: "Prepara i risultati finali" },
  { id: 5,  emoji: "📋", label: "Inserimento Risultati",      hint: "Compila piazzamenti competizioni" },
  { id: 6,  emoji: "💰", label: "Calcolo Economico",          hint: "Bonus stadio + premi automatici" },
  { id: 7,  emoji: "⭐", label: "Aggiornamento Ranking",      hint: "Verifica ranking storico" },
  { id: 8,  emoji: "🔓", label: "Apertura Rinnovi",           hint: "Manager decidono rinnovi/svincoli" },
  { id: 9,  emoji: "✅", label: "Approvazione Rinnovi",       hint: "Admin approva rinnovi" },
  { id: 10, emoji: "🎯", label: "Asta Furti",                 hint: "Sessione asta post-rinnovi" },
  { id: 11, emoji: "🧾", label: "Chiusura Economica",         hint: "Ricalcolo budget definitivo" },
  { id: 12, emoji: "📦", label: "Archiviazione",              hint: "Rose e trofei in archivio" },
  { id: 13, emoji: "🏆", label: "Albo d'Oro",                 hint: "Aggiornamento palmares" },
  { id: 14, emoji: "🌟", label: "Nuova Stagione",             hint: "Ciclo ricomincia da fase 1" },
];

function RosaSparklineTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-lg bg-slate-950/95 border border-slate-700 px-2.5 py-1.5 text-xs shadow-2xl">
      <div className="text-amber-300 font-bold font-mono">{r.season}{r.current ? " (attuale)" : ""}</div>
      <div className="text-slate-200 font-mono">Costo: <b>{Math.round(r.roster_cost)} cr</b></div>
      <div className="text-slate-500 font-mono text-[10px]">{r.n_players} giocatori</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // 'active' | 'archive'
  const [isMagnatiAdmin, setIsMagnatiAdmin] = useState(false);
  const [myTeam, setMyTeam] = useState(null);
  const [magnatiPhase, setMagnatiPhase] = useState(null); // phase number
  const [showPassword, setShowPassword] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/leagues"); setLeagues(data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // Check if user is admin/co-admin of Magnati league + current phase
    api.get("/magnati/lega").then(r => {
      setIsMagnatiAdmin(!!r.data?.is_admin);
      if (typeof r.data?.phase === "number") setMagnatiPhase(r.data.phase);
    }).catch(() => {});
    // Fetch personal team widget data
    api.get("/magnati/my-team").then(r => { if (r.data?.has_team) setMyTeam(r.data); }).catch(() => {});
  }, []);

  const { active, archived } = useMemo(() => {
    const a = [], b = [];
    for (const l of leagues) { (l.archived ? b : a).push(l); }
    return { active: a, archived: b };
  }, [leagues]);

  const shown = view === "archive" ? archived : active;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      <nav className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="dash-logo">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Trophy size={18} className="text-slate-900" />
            </div>
            <div>
              <div className="font-black uppercase tracking-wider text-lg leading-none">Fanta Business</div>
              <div className="text-[10px] tracking-[0.3em] text-slate-500 uppercase hidden sm:block">ERP fantasy manager</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 hidden sm:inline" data-testid="dash-user-email">{user?.email}</span>
            <button onClick={() => setShowPassword(true)} className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-2xl border border-slate-700 hover:border-amber-500 hover:text-amber-400 transition-colors" data-testid="dash-change-password-btn">
              <KeyRound size={14} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-2xl border border-slate-700 hover:border-rose-500 hover:text-rose-400 transition-colors" data-testid="dash-logout-btn">
              <LogOut size={14} /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold mb-2 flex items-center gap-2">
              <Sparkles size={14} /> {view === "archive" ? "Archivio anni passati" : "Le tue leghe"}
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight" data-testid="dash-title">
              Ciao <span className="text-amber-400">{user?.name}</span> <span className="wave">👋</span>
            </h1>
            <p className="text-slate-400 mt-2 text-sm sm:text-base">
              {view === "archive"
                ? "Leghe conservate per riferimento storico."
                : "Le società della stagione in corso."}
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            {view === "archive" ? (
              <button onClick={() => setView("active")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-700 hover:border-amber-400 hover:text-amber-300 transition-colors font-bold shadow-md shadow-black/40"
                data-testid="dash-back-active">
                <ArrowLeft size={16} /> In corso
              </button>
            ) : (
              <>
                <button onClick={() => setView("archive")} disabled={archived.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-700 hover:border-purple-500 hover:text-purple-300 transition-colors font-bold disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-black/40"
                  data-testid="dash-archive-btn">
                  <Archive size={16} /> Archivio {archived.length > 0 && <span className="text-xs bg-purple-500/20 px-2 py-0.5 rounded-full">{archived.length}</span>}
                </button>
                <Link to="/storico-magnati" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/5 hover:border-amber-400 hover:text-amber-300 text-amber-400 transition-colors font-bold shadow-md shadow-black/40" data-testid="dash-storico-magnati-btn">
                  🏆 Storico
                </Link>
                {(user?.role === "admin" || isMagnatiAdmin) && (
                  <Link to="/admin/magnati" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-rose-500/30 bg-rose-500/5 hover:border-rose-400 hover:text-rose-300 text-rose-400 transition-colors font-bold shadow-md shadow-black/40" data-testid="dash-admin-magnati-btn">
                    ⚙️ Admin Lega
                  </Link>
                )}
                {user?.role === "superadmin" && (
                  <Link to="/superadmin" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-pink-500/40 bg-gradient-to-br from-pink-500/10 to-purple-500/10 hover:from-pink-500/20 hover:to-purple-500/20 text-pink-300 hover:text-pink-200 transition-colors font-bold shadow-md shadow-black/40" data-testid="dash-superadmin-btn">
                    <ShieldCheck size={16} /> Super Admin
                  </Link>
                )}
                <Link to="/lega/unisciti" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 transition-colors font-bold shadow-md shadow-black/40" data-testid="dash-join-btn">
                  <LogIn size={16} /> Unisciti
                </Link>
                <Link to="/lega/nuova" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 hover:from-amber-200 hover:to-amber-400 transition-colors font-bold shadow-lg shadow-amber-500/40" data-testid="dash-create-btn">
                  <Plus size={16} /> Crea Lega
                </Link>
              </>
            )}
          </div>
        </div>

        {/* La tua squadra (Magnati widget) + Timeline fasi */}
        {myTeam && myTeam.has_team && view === "active" && (
          <div className="mb-8 grid gap-4 lg:grid-cols-3">
            <Link to={`/lega/${myTeam.league_id}`}
              data-testid="dash-my-team-widget"
              className="lg:col-span-2 block rounded-3xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-950 p-5 shadow-xl hover:border-amber-400 hover:shadow-amber-500/20 transition-all">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/40 shrink-0">
                  <Trophy size={28} className="text-slate-900" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400/80 mb-1">La tua squadra · {myTeam.league_name}</div>
                  <div className="text-2xl sm:text-3xl font-black text-amber-100 mb-3">{myTeam.team_name}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Budget</div>
                      <div className="text-xl font-bold text-amber-300">{Math.round(myTeam.budget)}<span className="text-xs text-slate-500 ml-1">cr</span></div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Valore Rosa</div>
                      <div className="text-xl font-bold text-emerald-300">{Math.round(myTeam.rosa_value)}<span className="text-xs text-slate-500 ml-1">cr</span></div>
                      {myTeam.rosa_cost != null && (
                        <div className="text-[9px] font-mono text-slate-500 mt-0.5" title="Somma prezzi d'acquisto (investimento reale)">
                          costo: {Math.round(myTeam.rosa_cost)} cr
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Giocatori</div>
                      <div className="text-xl font-bold text-slate-200">{myTeam.roster_count}</div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Ranking All-Time</div>
                      <div className="text-xl font-bold text-cyan-300">{myTeam.ranking.toLocaleString("it-IT")}</div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Trofei</div>
                      <div className="text-xl font-bold text-amber-300 flex items-center gap-1">
                        {myTeam.total_trophies} <Trophy size={14} className="text-amber-400 inline"/>
                      </div>
                    </div>
                  </div>
                  {myTeam.total_trophies > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(myTeam.palmares).map(([k, arr]) => (arr || []).length > 0 && (
                        <span key={k} className="text-xs px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200">
                          {k === "campionato" && "🏆 Campionato"}{k === "coppa_di_lega" && "🥇 Coppa di Lega"}
                          {k === "champions_league" && "🌟 Champions"}{k === "supercoppa" && "👑 Supercoppa"}
                          <span className="ml-1 font-mono text-amber-100">×{arr.length}</span>
                          <span className="ml-1 text-[10px] text-amber-400">({arr.join(", ")})</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Andamento Costo Rosa: badge Δ + sparkline */}
                  {(myTeam.rosa_history || []).length >= 2 && (
                    <div className="mt-4 pt-3 border-t border-amber-500/20" data-testid="rosa-history-widget">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                          📈 Andamento Costo Rosa
                        </div>
                        {myTeam.delta_vs_prev && (() => {
                          const d = myTeam.delta_vs_prev;
                          const up = d.direction === "up";
                          const flat = d.direction === "flat";
                          const Icon = flat ? MinusIcon : (up ? TrendingUp : TrendingDown);
                          const cls = flat
                            ? "bg-slate-800 border-slate-700 text-slate-300"
                            : up ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                 : "bg-rose-500/15 border-rose-500/40 text-rose-300";
                          const sign = d.abs > 0 ? "+" : "";
                          return (
                            <span data-testid="rosa-delta-badge"
                              title={`Rispetto a fine stagione ${d.prev_season}: ${sign}${d.abs} cr (${sign}${d.pct}%)`}
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-mono font-bold ${cls}`}>
                              <Icon className="w-3 h-3" strokeWidth={2.5} />
                              {sign}{d.pct}% vs {d.prev_season}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="h-24 -mx-2 sm:-mx-1">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={myTeam.rosa_history} margin={{ top: 8, right: 16, bottom: 4, left: 16 }}>
                            <defs>
                              <linearGradient id="rosaGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.55}/>
                                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02}/>
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="season" stroke="#64748b" fontSize={9} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <RechartTooltip content={<RosaSparklineTooltip />} />
                            <Area type="monotone" dataKey="roster_cost" stroke="#f59e0b" strokeWidth={2}
                              fill="url(#rosaGrad)" dot={{ r: 3, fill: "#f59e0b", stroke: "#0f172a", strokeWidth: 2 }}
                              activeDot={{ r: 5 }} isAnimationActive animationDuration={800} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Estratto Conto */}
                  {(myTeam.recent_movements || []).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-amber-500/20" data-testid="my-team-movements">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80 mb-2 flex items-center gap-1.5">
                        <Coins size={11}/> Estratto conto · ultimi movimenti
                      </div>
                      <div className="space-y-1">
                        {myTeam.recent_movements.map((m, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-slate-950/40 rounded-md px-2 py-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="shrink-0">
                                {m.type === "stadium" && "🏟️"}
                                {m.type === "adjust" && "⚙️"}
                                {m.type === "op" && "🔄"}
                              </span>
                              <span className="text-slate-300 truncate">{m.label}</span>
                              {m.reason && <span className="text-[10px] text-slate-500 truncate italic">· {m.reason}</span>}
                            </div>
                            <span className={`font-mono font-bold shrink-0 ${m.delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                              {m.delta >= 0 ? "+" : ""}{typeof m.delta === "number" ? m.delta.toFixed(1) : m.delta}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Stadio</div>
                  <div className="text-3xl">🏟️</div>
                  <div className="text-xs font-mono text-slate-400">Liv. {myTeam.stadium_level || "?"}</div>
                </div>
              </div>
            </Link>

            {/* Timeline fasi */}
            {magnatiPhase !== null && (
              <div className="rounded-3xl border border-indigo-500/30 bg-gradient-to-br from-indigo-950/30 via-slate-900 to-slate-950 p-4 shadow-xl" data-testid="dash-phase-timeline">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-indigo-400/80 mb-2">📅 Prossime fasi</div>
                {[magnatiPhase, magnatiPhase + 1, magnatiPhase + 2].filter(p => p <= 14).map((p, idx) => {
                  const meta = PHASE_META_SHORT[p];
                  if (!meta) return null;
                  const isCurrent = idx === 0;
                  return (
                    <div key={p}
                      data-testid={`dash-phase-${p}`}
                      className={`flex items-start gap-2 p-2 rounded-lg mb-1.5 ${
                        isCurrent
                          ? "bg-amber-500/15 border border-amber-500/40"
                          : "bg-slate-950/40 border border-slate-800"
                      }`}>
                      <span className="text-lg leading-none shrink-0">{meta.emoji}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono opacity-60">#{String(p).padStart(2,'0')}</span>
                          <span className={`text-xs font-bold ${isCurrent ? "text-amber-200" : "text-slate-300"}`}>{meta.label}</span>
                          {isCurrent && <span className="ml-auto text-[9px] px-1 rounded bg-amber-400 text-amber-950 font-mono">ORA</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{meta.hint}</div>
                      </div>
                    </div>
                  );
                })}
                <Link to={`/lega/${myTeam.league_id}`} className="mt-2 block text-center text-[10px] font-mono uppercase tracking-widest text-indigo-400 hover:text-indigo-300">
                  Vai al workflow →
                </Link>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-slate-500 text-sm uppercase tracking-widest">Caricamento…</div>
        ) : shown.length === 0 ? (
          <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-900/40 p-12 text-center shadow-xl shadow-black/40" data-testid="dash-empty">
            {view === "archive" ? (
              <>
                <Archive size={48} className="text-purple-400 mx-auto mb-4" />
                <div className="font-bold uppercase text-2xl mb-2">Archivio vuoto</div>
                <p className="text-slate-400 mb-6">Le leghe archiviate compariranno qui.</p>
                <button onClick={() => setView("active")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-700 hover:border-amber-400 font-bold" data-testid="empty-back-active">
                  <ArrowLeft size={16}/> Torna a &quot;In corso&quot;
                </button>
              </>
            ) : (
              <>
                <ShieldCheck size={48} className="text-emerald-500 mx-auto mb-4" />
                <div className="font-bold uppercase text-2xl mb-2">Nessuna lega ancora</div>
                <p className="text-slate-400 mb-6">Crea la tua prima lega o entra con un codice invito.</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Link to="/lega/nuova" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 font-bold shadow-lg shadow-amber-500/40" data-testid="empty-create-first">Crea la prima lega</Link>
                  <Link to="/lega/unisciti" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-700 hover:border-emerald-500 font-bold" data-testid="empty-join">Ho un codice</Link>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {shown.map((lg) => (
              <Link key={lg.league_id} to={`/lega/${lg.league_id}`}
                className="group border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-900/40 p-6 hover:border-amber-400/60 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 shadow-xl shadow-black/40"
                data-testid={`league-card-${lg.league_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {logoSrc(lg.logo_url)
                      ? <img src={logoSrc(lg.logo_url)} alt="" className="w-11 h-11 rounded-2xl object-cover border border-slate-700 shadow-md"/>
                      : <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500/30 to-amber-700/10 border border-amber-500/30 flex items-center justify-center text-xl shadow-md">🏆</div>}
                    <div className="text-xs tracking-[0.2em] text-emerald-400 uppercase font-bold flex items-center gap-1">
                      ⚽ Stagione {lg.season}
                    </div>
                  </div>
                  {lg.admin_id === user?.user_id && (
                    <span className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-full bg-amber-400/20 text-amber-300 border border-amber-500/30 shadow shadow-amber-500/10">👑 Admin</span>
                  )}
                </div>
                <div className="text-2xl font-black uppercase mb-2 tracking-tight group-hover:text-amber-400 transition-colors truncate">{lg.name}</div>
                <div className="font-mono text-[11px] text-slate-500 mb-4">CODICE · {lg.code}</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <MiniStat icon={<Users size={14} />} label="Squadre" v={lg.settings?.num_teams} />
                  <MiniStat icon={<Coins size={14} />} label="Budget" v={lg.settings?.initial_budget} />
                </div>
                {lg.archived && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-1">
                    <Archive size={11}/> Archiviata
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
      <ChangePasswordModal open={showPassword} onClose={() => setShowPassword(false)} />
    </div>
  );
}

function MiniStat({ icon, label, v }) {
  return (
    <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-3 shadow-inner">
      <div className="text-[10px] tracking-widest text-slate-500 uppercase flex items-center gap-1">{icon} {label}</div>
      <div className="font-mono font-bold text-lg mt-1">{v ?? "—"}</div>
    </div>
  );
}
